import { HelpCircle } from "lucide-react";

import {
  NonFousableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/tooltip";

import type { DropdownFilterType } from "../types";

import { useProjectRequestStatistics } from "../hooks/useProjectRequestStatistics";

interface ProjectRequestsStatisticsProps {
  onFilterClick?: (filter: DropdownFilterType) => void;
  projectId: number;
}

export function ProjectRequestsStatistics({
  onFilterClick,
  projectId,
}: ProjectRequestsStatisticsProps) {
  const { data: statistics } = useProjectRequestStatistics(projectId);

  const requestCounts = statistics?.counts;
  const activeSlots = statistics?.activeSlots;

  const totalActiveSlots = activeSlots
    ? (activeSlots.graphic || 0) +
      (activeSlots.video || 0) +
      (activeSlots.website || 0)
    : 0;

  const buildTooltipContent = () => {
    const parts = [];

    if (activeSlots?.graphic) {
      parts.push(`${activeSlots.graphic} graphic`);
    }

    if (activeSlots?.video) {
      parts.push(`${activeSlots.video} video`);
    }

    if (activeSlots?.website) {
      parts.push(`${activeSlots.website} website`);
    }

    return parts.length > 0 ? parts.join(", ") : "No active requests";
  };

  return (
    <div className="flex col-span-2 items-center justify-evenly w-full text-sm divide-x divide-border-secondary">
      <div className="flex flex-col items-center px-6 py-4 lg:border-l border-border-secondary">
        <div className="flex items-center gap-1">
          <span className="text-text-tertiary text-nowrap">Design slots</span>
          <TooltipTrigger delay={250}>
            <NonFousableTooltipTarget>
              <HelpCircle className="size-3 text-text-tertiary hover:text-text-secondary" />
            </NonFousableTooltipTarget>
            <Tooltip>
              The number of design requests being worked on simultaneously
            </Tooltip>
          </TooltipTrigger>
        </div>
        <TooltipTrigger delay={250}>
          <NonFousableTooltipTarget>
            <button
              className="font-semibold text-text-warning-primary text-2xl hover:text-orange-600 cursor-pointer focus:outline-none focus:underline"
              onClick={() => onFilterClick?.("active")}
              type="button"
            >
              {totalActiveSlots}/{activeSlots?.maxTotal || 6}
            </button>
          </NonFousableTooltipTarget>
          <Tooltip>{buildTooltipContent()}</Tooltip>
        </TooltipTrigger>
      </div>

      <div className="flex flex-col items-center px-6 py-4">
        <span className="text-text-tertiary">Queued</span>
        <button
          className="font-semibold text-text-primary text-2xl hover:text-text-secondary cursor-pointer focus:outline-none focus:underline"
          onClick={() => onFilterClick?.("queued")}
          type="button"
        >
          {requestCounts?.queued || 0}
        </button>
      </div>

      <div className="flex flex-col items-center px-6 py-3">
        <span className="text-text-tertiary">Completed</span>
        <button
          className="font-semibold text-text-success-primary text-2xl hover:text-green-700 cursor-pointer focus:outline-none focus:underline"
          onClick={() => onFilterClick?.("completed")}
          type="button"
        >
          {requestCounts?.completed || 0}
        </button>
      </div>
    </div>
  );
}
